import { useInfiniteQuery, useQueries, useQuery } from "@tanstack/react-query";
import type { getCusomterType } from "./types.js";
import { GetAllAverageCustomerApi, GetCustomerInfo, GetCustomersFn, GetCustomersListVisitor, GetPlaneCustomer, GetPlaneCustomerTest, GetVisitorRequests } from "./api.js";
import { GetAverageCustomerKey, GetAverageVisitorKey, GetCustomer, getCustomerInfo, GetCustomerListVisitorkey, GetPlaneCustomerKey } from "./key.js";


export const useGetCustomer = (params: getCusomterType) => {
  return useQuery({
    queryFn: () => GetCustomersFn(params),
    queryKey: GetCustomer(params)
  })
}

export const useGetCustomerInfo = (mobile: number) => {
  return useQuery({
    queryFn: () => GetCustomerInfo(mobile),
    queryKey: getCustomerInfo()
  })
}

export const useGetPlaneCustomer = () => {
  return useQuery({
    queryFn: () => GetPlaneCustomer(),
    queryKey: GetPlaneCustomerKey(),
    refetchOnMount: true
  })
}


// ✅ اضافه شدن پارامتر enabled
export const useGetPlaneCustomerWithEnable = (options: any) => {
  return useQuery({
    queryKey: GetPlaneCustomerKey(),
    queryFn: GetPlaneCustomer,
    enabled: options?.enabled ?? true,
  });
};

export const useGetPlaneCustomerTest = () => {
  return useQuery({
    queryFn: () => GetPlaneCustomerTest(),
    queryKey: GetPlaneCustomerKey()
  })
}

export const useGetPlaneCustomersProfileWithEnabled = (options?: any) => {
  return useQuery({
    queryFn: () => GetPlaneCustomerTest(),
    queryKey: GetPlaneCustomerKey(),
    enabled: options?.enabled ?? true, // پیش‌فرض true
    ...options,
    refetchOnMount: true
  })
}


export const useGetCustomerListVisitor = (search?: string) => {
  return useInfiniteQuery({
    queryKey: [...GetCustomerListVisitorkey(), search],
    queryFn: ({ pageParam = 0 }) =>
      GetCustomersListVisitor({
        page: pageParam,
        size: 25,
        search: search || "",
        sortField: "id",
        sortDirection: "ASC",
      }),
    getNextPageParam: (lastPage, allPages) => {
      // اگر تعداد آیتم‌های دریافتی برابر با سایز باشد، صفحه بعدی وجود دارد
      const totalCount = lastPage?.count || 0;
      const loadedItems = allPages.reduce(
        (acc, page) => acc + (page?.payload?.length || 0),
        0
      );

      if (loadedItems < totalCount) {
        return allPages.length; // صفحه بعدی
      }
      return undefined; // تمام شد
    },
    initialPageParam: 0,
    staleTime: 0,
    refetchOnMount: "always",
    //   refetchOnWindowFocus: true,
    //   refetchOnReconnect: true,
  });
};


// core/services/customer/diamonds/queries.ts

import { GetActiveDiamondValue } from "./api.js"
import { GetAllAverageVisitorApi } from "./api.js";

export const useGetAllAverageVisitor = (
  options: { enabled?: boolean } = {}
) => {
  return useQuery({
    queryKey: GetAverageVisitorKey(),
    queryFn: () => GetAllAverageVisitorApi(),
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    retry: true,
  });
};
// core/services/auth/queris.ts

export const useGetAllAverageCustomer = (options: { enabled?: boolean } = {}) => {
  return useQuery({
    queryKey: GetAverageCustomerKey(),
    queryFn: () => GetAllAverageCustomerApi(),
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    retry: true,
  });
};
export const useGetActiveDiamondValue = () => {
  return useQuery({
    queryKey: ["activeDiamondValue"],
    queryFn: GetActiveDiamondValue,
  })
}
// ============================================================
// ⭐ هوک دریافت لیست درخواست‌ها با صفحه‌بندی
// ============================================================
export const useGetVisitorRequests = (
  status: "ALL" | "NEW" | "APPROVED" | "REJECTED" | "PENDING" = "ALL",
  size: number = 25
) => {
  return useInfiniteQuery({
    queryKey: ["visitorRequests", status],
    queryFn: ({ pageParam = 0 }) =>
      GetVisitorRequests({
        status,
        page: pageParam,
        size,
        sortField: "id",
        sortDirection: "DESC",
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage: any, allPages: any[]) => {
      const totalFetched = allPages.reduce(
        (acc, page) => acc + (page?.payload?.length || 0),
        0
      );
      const totalCount = lastPage?.count || 0;
      if (totalFetched < totalCount) {
        return allPages.length;
      }
      return undefined;
    },
    staleTime: 1000 * 60,
  });
};

import { GetPlanForCustomer } from "./api.js";
import { GetPlanForCustomerKey } from "./key.js";

// ============================================================
// ✅ دریافت طرح‌های یک مشتری
// ============================================================
export const useGetPlanForCustomer = (
  customerId: number,
  options?: { enabled?: boolean }
) => {
  return useQuery({
    queryKey: GetPlanForCustomerKey(customerId),
    queryFn: () => GetPlanForCustomer(customerId),
    enabled: options?.enabled ?? !!customerId,
    staleTime: 0,
    refetchOnMount: "always",
  });
};

import { GetVisitorPerformance } from "./api.js";
import { GetVisitorPerformanceKey } from "./key.js";

// ============================================================
// ✅ گزارش عملکرد بازاریاب با اینفینیتی اسکرول
// ============================================================
export const useGetVisitorPerformance = (size = 25) => {
  return useInfiniteQuery({
    queryKey: [...GetVisitorPerformanceKey()],
    queryFn: ({ pageParam = 0 }) =>
      GetVisitorPerformance({
        page: pageParam,
        size,
        sortField: "id",
        sortDirection: "DESC",
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage: any, allPages: any[]) => {
      const totalFetched = allPages.reduce(
        (acc, page) => acc + (page?.payload?.length || 0),
        0
      );
      const totalCount = lastPage?.count || 0;
      if (totalFetched < totalCount) {
        return allPages.length;
      }
      return undefined;
    },
  });
};

import { GetCustomerPerformance } from "./api.js";
import { GetCustomerPerformanceKey } from "./key.js";

// ============================================================
// ✅ گزارش عملکرد مشتری با اینفینیتی اسکرول
// ============================================================
export const useGetCustomerPerformance = (size = 25) => {
  return useInfiniteQuery({
    queryKey: [...GetCustomerPerformanceKey()],
    queryFn: ({ pageParam = 0 }) =>
      GetCustomerPerformance({
        page: pageParam,
        size,
        sortField: "id",
        sortDirection: "DESC",
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage: any, allPages: any[]) => {
      const totalFetched = allPages.reduce(
        (acc, page) => acc + (page?.payload?.length || 0),
        0
      );
      const totalCount = lastPage?.count || 0;
      if (totalFetched < totalCount) {
        return allPages.length;
      }
      return undefined;
    },
  });
};

import { GetCustomerDetailForVisitor } from "./api.js";
import { GetCustomerDetailForVisitorKey } from "./key.js";

// ============================================================
// ✅ جزییات یک مشتری برای بازاریاب
// ============================================================
export const useGetCustomerDetailForVisitor = (customerId: number) => {
  return useQuery({
    queryKey: GetCustomerDetailForVisitorKey(customerId),
    queryFn: () => GetCustomerDetailForVisitor(customerId),
    enabled: !!customerId,
  });
};